/**
 * What the action on a piece says to its host.
 *
 * Extracted from `piece-card.tsx` on 2026-10-08, unchanged, because a second surface started
 * saying something else about the same piece: the PDP's "Componha o seu presente" grid offered
 * the host "Presentear" — the guest's word — on pieces of their own list (reported from
 * staging). The panel's grid and the PDP's grid are the same question asked twice, so the answer
 * lives in one place now.
 *
 * Three vocabularies, because they are three different things. The first two are acts, and only
 * a real control may speak them:
 *
 * - **curating** (`modo="curation"`, the creation's catalog): the piece is being *chosen*, and
 *   the button is a toggle — "Na sua lista" once it is in, "Adicionar à lista" while it is out;
 * - **managing** (Minha Lista): the piece is being *arranged*, and the button opens the pack
 *   dialog — "Editar conjunto" for a pack, "Editar quantidade" for a single piece, "Adicionar"
 *   for one not on the list yet;
 * - **navigating** (`pieceDestinationLabel`, below): the surface is a link and nothing else, so
 *   it may only *state* something. A word borrowed from the two above would promise an act the
 *   surface cannot perform — which is exactly what happened to the PDP's suggestion grid on
 *   2026-10-08: it started saying "Editar quantidade" over a card whose click navigates, and no
 *   dialog was ever going to open.
 */

/** Which act the surface is in. */
export type PieceActionMode = "curation" | "management";

/**
 * The piece, as far as this answer is concerned: whether it is on the list, and how many units
 * one gift of it is (`pack`, the bundle multiple; `1` for a single piece).
 */
export type PieceActionState = {
  readonly inList: boolean;
  readonly pack: number;
};

export function pieceActionLabel(mode: PieceActionMode, piece: PieceActionState): string {
  if (mode === "curation") {
    return piece.inList ? "Na sua lista" : "Adicionar à lista";
  }

  if (!piece.inList) return "Adicionar";

  return piece.pack > 1 ? "Editar conjunto" : "Editar quantidade";
}

/**
 * What a surface that only **navigates** may say about a piece — a card whose whole body is a
 * link, like the PDP's "Componha o seu presente" grid.
 *
 * A state, never an act. "Na sua lista" is the same word the curation uses for the same fact, so
 * the host reads one vocabulary across the product; and a piece the list does not hold gets the
 * plain description of what the click does, because there is no state worth announcing.
 *
 * The day that grid gains a real control, it stops calling this and starts calling
 * `pieceActionLabel("management", …)` — the words are already there, waiting for a button that
 * can keep their promise.
 */
export function pieceDestinationLabel(piece: PieceActionState): string {
  return piece.inList ? "Na sua lista" : "Ver peça";
}
